import { Observable, type OperatorFunction } from 'rxjs';
import { UpdateActionEnum } from '../types';
import type { MinimalSubscriptionResponse } from '../types/SubscriptionResponse';

interface wsStitchWithParams<PrimaryType, SecondaryType, OutputType> {
  /** The unique key to reference Primary entities by. If there is not a PrimaryStitchKey provided, this key will also be used for stitching. */
  getPrimaryTypeKey: (item: PrimaryType) => string;
  /**
   * Key used to match primary entities to secondary entities. Defaults to `getPrimaryTypeKey`.
   * Unlike `getPrimaryTypeKey`, this key does NOT need to be unique across primary entities,
   * enabling many-to-one stitching (e.g. multiple primary entities sharing the same stitch key).
   * Returning `undefined` means the primary entity is not eligible for stitching.
   *
   * Note: Stitch keys need to be stable.
   */
  getPrimaryTypeStitchKey?: (item: PrimaryType) => string | undefined;
  getSecondaryTypeKey: (item: SecondaryType) => string;
  secondarySource: Observable<MinimalSubscriptionResponse<SecondaryType>>;
  stitch: (primaryItem: PrimaryType, secondaryItem: SecondaryType | undefined) => OutputType;
}

/**
 * `wsStitchWith` is used to stitch data from two sources together.
 *
 * The primary source (the observable we are piped off of) is the data source determining the output records.
 * The secondary source data will only ever enrich primary source records.
 *
 * If `getPrimaryTypeStitchKey` is provided, the stitch key can differ from the storage key. This allows
 * many primary entities to share the same stitch key (many:1 stitching), while still being uniquely stored
 * by their `getPrimaryTypeKey`.
 *
 * Not supported: `UpdateAction.Delete` messages. This pipe does not work with streams which rely on the frontend understanding deletion messages.
 *
 * Future work:
 * - Extend the pipe to allow an array of secondarySources
 */
export function wsStitchWith<PrimaryType, SecondaryType, OutputType>({
  getPrimaryTypeKey,
  getPrimaryTypeStitchKey,
  getSecondaryTypeKey,
  secondarySource,
  stitch,
}: wsStitchWithParams<PrimaryType, SecondaryType, OutputType>): OperatorFunction<
  MinimalSubscriptionResponse<PrimaryType>,
  MinimalSubscriptionResponse<OutputType>
> {
  const getStitchKey = getPrimaryTypeStitchKey ?? getPrimaryTypeKey;

  return primarySource => {
    return new Observable<MinimalSubscriptionResponse<OutputType>>(output => {
      const primaryEntities = new Map<string, PrimaryType>();
      // Index: stitchKey → Set of storageKeys (for looking up which primaries match a secondary)
      const stitchKeyToPrimaryKeys = new Map<string, Set<string>>();
      let secondaryEntities = new Map<string, SecondaryType>();
      let latestPrimaryTag: string | undefined;

      const primarySub = primarySource.subscribe(message => {
        const outputData: OutputType[] = [];

        if (message.initial) {
          primaryEntities.clear();
          stitchKeyToPrimaryKeys.clear();
        }

        for (const update of message.data) {
          const storageKey = getPrimaryTypeKey(update);
          const stitchKey = getStitchKey(update);

          // @ts-expect-error - needs stronger typing clarity - UpdateAction not tied to PrimaryType's base
          const maybeUpdateAction: UpdateActionEnum | undefined = update['UpdateAction'];
          const isDeletion = maybeUpdateAction === UpdateActionEnum.Remove;

          if (isDeletion) {
            // Delete the previously stored entity from the map, but still forward the Deletion message downstream
            primaryEntities.delete(storageKey);
            if (stitchKey != null) {
              removeFromKeyMapping(stitchKeyToPrimaryKeys, storageKey, stitchKey);
            }
            outputData.push(stitch(update, stitchKey != null ? secondaryEntities.get(stitchKey) : undefined));
          } else {
            // Normal Update
            primaryEntities.set(storageKey, update);
            if (stitchKey != null) {
              addToKeyMapping(stitchKeyToPrimaryKeys, storageKey, stitchKey);
            }
            outputData.push(stitch(update, stitchKey != null ? secondaryEntities.get(stitchKey) : undefined));
          }
        }

        latestPrimaryTag = message.tag;
        output.next({
          tag: message.tag,
          initial: message.initial,
          data: outputData,
        });
      });

      const secondarySub = secondarySource.subscribe(message => {
        const outputData: OutputType[] = [];

        if (message.initial) {
          // If we receive an initial: true message on the secondary stream, we need to remove all stitching we have done so to speak and re-apply new ones.
          // For each stitch we have done previously, we'll have an entry currently in secondaryEntities map. Using that, we can resolve which
          // primaryEntities we need to send un-stitched updates for.
          const newSecondaryEntities = new Map<string, SecondaryType>(
            message.data.map(update => [getSecondaryTypeKey(update), update])
          );

          // Create a unique list of all the relevant keys. The previously-stitched secondary entities and the new ones.
          const allSecondaryEntityKeys = new Set([...secondaryEntities.keys(), ...newSecondaryEntities.keys()]);
          for (const key of allSecondaryEntityKeys) {
            const matchingPrimaries = resolvePrimaryEntities(stitchKeyToPrimaryKeys, primaryEntities, key);
            for (const primaryEntity of matchingPrimaries) {
              // We'll either stitch with the new secondary entity or undefined.
              outputData.push(stitch(primaryEntity, newSecondaryEntities.get(key)));
            }
          }

          // Overwrite the old (now outdated) secondary entities with the new ones.
          secondaryEntities = newSecondaryEntities;
        } else {
          // Message not initial. What we do here is we just for each secondary entity see what primary entities we need to send updates for.
          for (const update of message.data) {
            const key = getSecondaryTypeKey(update);
            // @ts-expect-error - needs stronger typing clarity - UpdateAction is not tied to SecondaryType's base
            const maybeUpdateAction: UpdateActionEnum | undefined = update['UpdateAction'];
            const isDeletion = maybeUpdateAction === UpdateActionEnum.Remove;

            if (isDeletion) {
              secondaryEntities.delete(key);
              const matchingPrimaries = resolvePrimaryEntities(stitchKeyToPrimaryKeys, primaryEntities, key);
              for (const primaryEntity of matchingPrimaries) {
                outputData.push(stitch(primaryEntity, undefined)); // send the un-stitched entity out
              }
            } else {
              // Is a normal update operation
              secondaryEntities.set(key, update);
              const matchingPrimaries = resolvePrimaryEntities(stitchKeyToPrimaryKeys, primaryEntities, key);
              for (const primaryEntity of matchingPrimaries) {
                outputData.push(stitch(primaryEntity, update));
              }
            }
          }
        }

        if (outputData.length > 0) {
          output.next({
            tag: latestPrimaryTag,
            initial: false,
            data: outputData,
          });
        }
      });

      return () => {
        primarySub.unsubscribe();
        secondarySub.unsubscribe();
      };
    });
  };
}

/** Register a primary entity's storageKey under its stitchKey in the index. */
function addToKeyMapping(keyMapping: Map<string, Set<string>>, storageKey: string, stitchKey: string) {
  let set = keyMapping.get(stitchKey);
  if (!set) {
    set = new Set();
    keyMapping.set(stitchKey, set);
  }
  set.add(storageKey);
}

/** Remove a primary entity's storageKey from its stitchKey entry in the index. */
function removeFromKeyMapping(keyMapping: Map<string, Set<string>>, storageKey: string, stitchKey: string) {
  const set = keyMapping.get(stitchKey);
  if (set) {
    set.delete(storageKey);
    if (set.size === 0) {
      keyMapping.delete(stitchKey);
    }
  }
}

/** Look up all primary entities whose stitchKey matches the given key. */
function resolvePrimaryEntities<PrimaryType>(
  keyMapping: Map<string, Set<string>>,
  primaryEntities: Map<string, PrimaryType>,
  stitchKey: string
): PrimaryType[] {
  const storageKeys = keyMapping.get(stitchKey);
  if (!storageKeys) {
    return [];
  }
  const result: PrimaryType[] = [];
  for (const sk of storageKeys) {
    const entity = primaryEntities.get(sk);
    if (entity) {
      result.push(entity);
    }
  }
  return result;
}
